<template>
    <card-list-item @click="$emit('click')" :class="{'active': active}">
        <template v-slot:icon><i class="fa-fw" :class="[icon, active ? 'fas' : 'far']"></i></template>
        <template v-slot:content>
            <div class="tile-title">
                {{primary}}
                <span class="removed has-text-danger" v-if="contact.contact_deleted">removed from directory</span>
            </div>
            <div class="meta" v-if="secondary">{{secondary}}</div>
            <div class="meta">{{summary}}</div>
        </template>
        <template v-slot:status>
            <b-tag v-if="roleLabel">{{roleLabel}}</b-tag>
        </template>
        <template v-slot:details>
            <div class="details-rollout">
                <div class="data mt-3">
                    <data-row mini title="Type">{{typeLabel}}</data-row>
                    <data-row mini title="Role" v-if="contact.role">{{roleLabel}}</data-row>
                    <data-row mini title="Label" v-if="contact.label">{{contact.label}}</data-row>
                    <data-row mini title="Company" v-if="contact.company">{{contact.company}}</data-row>
                    <data-row mini title="Name" v-if="contact.name">{{contact.name}}</data-row>
                    <data-row mini title="Title" v-if="contact.title">{{contact.title}}</data-row>
                    <data-row mini title="Email" v-if="contact.email">{{contact.email}}</data-row>
                    <data-row mini title="Email 2" v-if="contact.email_2">{{contact.email_2}}</data-row>
                    <data-row mini title="Phone" v-if="contact.phone">{{contact.phone}}</data-row>
                    <data-row mini title="Phone 2" v-if="contact.phone_2">{{contact.phone_2}}</data-row>
                    <data-row mini title="Website" v-if="contact.website">{{contact.website}}</data-row>
                    <data-row mini title="Address" v-if="contact.address"><span style="white-space: pre-line">{{contact.address}}</span></data-row>
                    <data-row mini title="City" v-if="contact.city">{{contact.city}}</data-row>
                    <data-row mini title="Country" v-if="contact.country">{{contact.country}}</data-row>
                    <!-- Directory notes are true on every trip; link notes are
                         about this attachment only. Both shown, labelled apart. -->
                    <data-row mini title="Notes" v-if="contact.directory_notes"><span style="white-space: pre-line">{{contact.directory_notes}}</span></data-row>
                    <data-row mini title="This Use" v-if="contact.link_notes"><span style="white-space: pre-line">{{contact.link_notes}}</span></data-row>
                    <data-row mini title="ID">{{contact.id}}</data-row>
                </div>
            </div>
        </template>
    </card-list-item>
</template>

<script>
    import DataRow from "../TIER/components/DataRow";
    import CardListItem from "../TIER/components/CardListItem";
    import {typeIcon, typeLabel, roleLabel} from "@/components/contact_meta";

    export default {
        name: 'ContactListItem',
        components: {CardListItem, DataRow},
        props: {
            card: {
                required: true
            },
            // Either a directory row (from /api/contacts) or a joined link row
            // (from /api/contacts/entity/...), which carries the same contact
            // columns plus role/label/link_notes.
            contact: {
                type: Object,
                required: true
            },
            active: {
                type: Boolean,
                default: false
            },
            // Which of company/person leads the row. 'company' is the
            // directory-wide rule; a card that is already about one company
            // (the drill-down's company card) leads with the person instead.
            lead: {
                type: String,
                default: 'company'
            }
        },
        computed: {
            // The organisation if we have one, otherwise the person -- the same
            // display rule the server uses for entity_title.
            primary: function () {
                if (this.lead === 'name') return this.contact.name || this.contact.company || '(no name)';
                return this.contact.company || this.contact.name || '(no name)';
            },
            secondary: function () {
                if (this.lead === 'name') return this.contact.name ? this.contact.title : null;
                return this.contact.company ? this.contact.name : null;
            },
            summary: function () {
                return [this.contact.phone, this.contact.email].filter(Boolean).join(' · ');
            },
            icon: function () {
                return typeIcon(this.contact.type);
            },
            typeLabel: function () {
                return typeLabel(this.contact.type);
            },
            // A link shows its own role; a bare directory row shows its type.
            roleLabel: function () {
                if (this.contact.role) return roleLabel(this.contact.role, this.contact.type);
                return this.contact.link_id ? typeLabel(this.contact.type) : null;
            }
        }
    }
</script>

<style lang="scss" scoped>

    li {
        color: darken($t-primary, 10);

        .tile-title {
            font-size: .9rem;
            font-weight: 700;
        }

        padding: 0.5rem 0;

        .details-rollout {
            margin-top: 0.5rem;
        }
    }

    // A contact soft-deleted from the directory while still attached here. The
    // link is kept deliberately so the card doesn't silently lose a row.
    // Colour comes from Bulma's has-text-danger -- only $t-primary exists in
    // _variables.scss, and TaskListItem's overdue row does the same.
    .removed {
        font-size: 0.7rem;
        font-weight: 400;
        margin-left: 0.4rem;
    }
</style>
